import { useMemo } from 'react';

import type { HealthGuardSnapshot } from '@/types/surveillance';

type GuardState = 'healthy' | 'degraded' | 'unknown';

export const useHealthGuardState = (snapshot?: HealthGuardSnapshot | null) => {
  const guardState: GuardState = useMemo(() => {
    if (!snapshot) return 'unknown';
    if (typeof snapshot.state === 'string') {
      return snapshot.state as GuardState;
    }
    if (snapshot.healthy === false) return 'degraded';
    if (snapshot.healthy === true) return 'healthy';
    return 'unknown';
  }, [snapshot]);

  const guardReason = useMemo(() => {
    if (!snapshot) return '';
    if (typeof snapshot.reason === 'string') {
      return snapshot.reason;
    }
    if (snapshot.reason && typeof snapshot.reason === 'object') {
      const nestedReason = (snapshot.reason as { reason?: unknown }).reason;
      if (typeof nestedReason === 'string') {
        return nestedReason;
      }
    }
    if (typeof snapshot.system_health === 'string' && snapshot.system_health.trim()) {
      return snapshot.system_health;
    }
    return '';
  }, [snapshot]);

  const guardQueuedText = useMemo(() => {
    if (!snapshot) return '';
    if (typeof snapshot.queued !== 'number' || snapshot.queued <= 0) {
      return '';
    }
    return `${snapshot.queued} queued`;
  }, [snapshot]);

  const guardCpuText = useMemo(() => {
    if (!snapshot) return '';
    if (typeof snapshot.cpu !== 'number') {
      return '';
    }
    return `${Math.round(snapshot.cpu * 100)}% cpu`;
  }, [snapshot]);

  const guardReasonText = useMemo(() => {
    if (!snapshot) return '';
    return [guardReason, guardQueuedText, guardCpuText]
      .filter((part) => part && part.length > 0)
      .join(' · ');
  }, [snapshot, guardReason, guardQueuedText, guardCpuText]);

  return {
    guardState,
    guardReason,
    guardReasonText,
    guardQueuedText,
    guardCpuText,
  };
};
